import { Fragment, useEffect, useRef, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { GetCurrentPhaseTaperData, GetExerciseLocalCompletedSets, LoadSetWeightRecommendation } from 'functions/activeWorkoutUtils'
import { GetActiveWorkout, setActiveWorkoutIncompleteSetInfo, resetActiveWorkoutIncompleteSetInfo } from 'redux/slices/activeWorkout'
import { ActiveWorkoutContext } from 'context/ActiveWorkoutContext'
import store from 'redux/store'

import ScreenFrame from 'components/Frame/ScreenFrame'
import SolidButton from 'components/Buttons/SolidButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import ContentFrame from 'components/Frame/ContentFrame'
import PhaseHeader from 'components/ActiveWorkout/PhaseHeader'
import RoundDivider from 'components/ActiveWorkout/RoundDivider'
import ExerciseSetListItem from 'components/ActiveWorkout/ExerciseSetListItem'

export default function ActiveWorkout({ profileType }) {
    const dispatch = useDispatch()

    const { DiscardActiveWorkoutWarningModal, EndActiveWorkoutWarningModal } = useContext(ActiveWorkoutContext)

    const loadingRecommendationsRef = useRef(new Set()) // Track in-flight recommendation requests

    const { groupId: groupIdParam, athleteId: athleteIdParam } = useParams()
    const groupId = groupIdParam != null ? parseInt(groupIdParam) : null
    const athleteId = athleteIdParam != null ? parseInt(athleteIdParam) : null

    const { exercises } = useGetExercises()

    const activeWorkoutState = useSelector((state) => state.activeWorkout)
    const activeWorkout = GetActiveWorkout(activeWorkoutState, profileType, groupId, athleteId)

    const InitializeWorkoutWeightRecommendations = () => {

        if (!activeWorkout || !activeWorkout.workoutTemplate) return

        activeWorkout.workoutTemplate.phases.forEach((phase, phaseIndex) => {

            const phaseTaperData = GetCurrentPhaseTaperData(activeWorkout, phase, athleteId)

            phase.exercises.forEach((exercise, exerciseIndex) => {

                var localCompletedSets = []

                if (profileType === 'group') {
                    localCompletedSets = [] // TODO: Fix this??
                }

                if (profileType === 'individual') {
                    localCompletedSets = GetExerciseLocalCompletedSets(phaseTaperData, exercise)
                }

                exercise.sets.forEach((set, setIndex) => {

                    const requestKey = `${phaseIndex}-${exerciseIndex}-${setIndex}`

                    if (
                        set.completedSetData.completed === false
                        && set.recommendationData.weightRecommendationLoaded === false
                        && !loadingRecommendationsRef.current.has(requestKey) // Prevent duplicate calls
                    ) {
                        LoadSetWeightRecommendation(
                            dispatch,
                            activeWorkoutState,
                            profileType,
                            groupId,
                            athleteId,
                            phaseTaperData,
                            parseInt(phaseIndex),
                            parseInt(exerciseIndex),
                            parseInt(setIndex),
                            localCompletedSets,
                            setActiveWorkoutIncompleteSetInfo
                        ).finally(() => {
                            // console.log('Completed initializing recommendation for set: ', requestKey)
                            loadingRecommendationsRef.current.delete(requestKey)
                        })
                    }
                    
                })
            })
        })
    }

    const MutateRemainingSets = (payload) => {

        // Get the latest Redux state like the frontend does
        const latestActiveWorkoutState = store.getState().activeWorkout

        const latestWorkout = GetActiveWorkout(latestActiveWorkoutState, profileType, groupId, athleteId)
        
        if (!latestWorkout) return

        const currentPhase = latestWorkout.workoutTemplate.phases[payload.phaseIndex]
        const currentExercise = currentPhase.exercises[payload.exerciseIndex]

        const taperData = GetCurrentPhaseTaperData(latestWorkout, currentPhase)
        const localCompletedSets = GetExerciseLocalCompletedSets(taperData, currentExercise)

        for (let setIndex = 0; setIndex < currentExercise.sets.length; setIndex++) {
            
            var set = currentExercise.sets[setIndex]

            if (set.completedSetData.completed === false) {

                switch (payload.mutationKey) {
                    
                    case 'recalculate-weight-recommendation':

                        const resetRecommendationPayload = {
                            profileType: profileType,
                            groupId: groupId,
                            athleteId: athleteId,
                            phaseIndex: parseInt(payload.phaseIndex),
                            exerciseIndex: parseInt(payload.exerciseIndex),
                            setIndex: parseInt(setIndex),
                        }

                        dispatch(resetActiveWorkoutIncompleteSetInfo(resetRecommendationPayload))

                        const recalculationRequestKey = `${payload.phaseIndex}-${payload.exerciseIndex}-${setIndex}`

                        if (!loadingRecommendationsRef.current.has(recalculationRequestKey)) {

                            loadingRecommendationsRef.current.add(recalculationRequestKey)

                            LoadSetWeightRecommendation(
                                dispatch,
                                latestActiveWorkoutState,
                                profileType,
                                groupId,
                                athleteId,
                                taperData,
                                parseInt(payload.phaseIndex),
                                parseInt(payload.exerciseIndex),
                                parseInt(setIndex),
                                localCompletedSets,
                                setActiveWorkoutIncompleteSetInfo
                            ).finally(() => {
                                // console.log('Completed recalculation for set: ', recalculationRequestKey)
                                loadingRecommendationsRef.current.delete(recalculationRequestKey)
                            })

                        }

                        break;

                    /*
                    case 'mds-reps-adjustment':

                        if (setIndex !== payload.setIndex) {
                            const repBumpPayload = {
                                profileType: profileType,
                                groupId: groupId,
                                athleteId: athleteId,
                                phaseIndex: parseInt(payload.phaseIndex),
                                exerciseIndex: parseInt(payload.exerciseIndex),
                                setIndex: parseInt(setIndex),
                                bumpAmount: parseInt(payload.bumpAmount),
                            }

                            dispatch(updateActiveWorkoutSetRepsAssigned(repBumpPayload))
                        }

                        break;
                    */

                    /*
                    case 'lds-load-adjustment':

                        if (setIndex !== payload.setIndex) {

                            const resetPayload = {
                                profileType: profileType,
                                groupId: groupId,
                                athleteId: athleteId,
                                phaseIndex: parseInt(payload.phaseIndex),
                                exerciseIndex: parseInt(payload.exerciseIndex),
                                setIndex: parseInt(setIndex)
                            }

                            // console.log(resetPayload)

                            dispatch(resetActiveWorkoutIncompleteSetInfo(resetPayload))

                            const ldsRequestKey = `${payload.phaseIndex}-${payload.exerciseIndex}-${setIndex}`

                            if (!loadingRecommendationsRef.current.has(ldsRequestKey)) {

                                loadingRecommendationsRef.current.add(ldsRequestKey)

                                LoadSetWeightRecommendation(
                                    dispatch,
                                    latestActiveWorkoutState,
                                    profileType,
                                    groupId,
                                    athleteId,
                                    latestWorkout,
                                    taperData,
                                    parseInt(payload.phaseIndex),
                                    parseInt(payload.exerciseIndex),
                                    parseInt(setIndex),
                                    localCompletedSets,
                                    setActiveWorkoutIncompleteSetInfo
                                ).finally(() => {
                                    // console.log('Completed recalculation for set: ', ldsRequestKey)
                                    loadingRecommendationsRef.current.delete(ldsRequestKey)
                                })
                            }
                        }
                        
                        break;
                    */

                    default:
                        console.warn('Unknown mutation key:', payload.mutationKey)
                        break;
                }
            }
        }
    }

    useEffect(() => {
        if (activeWorkout && activeWorkout.workoutTemplate) {
            InitializeWorkoutWeightRecommendations()
        }
    }, [activeWorkout])

    if (exercises.length === 0 || !activeWorkout || !activeWorkout.workoutTemplate) {
        return (
            <ScreenFrame limitWidth={false} alwaysHideResumeWorkoutHeader={true}>
                <div className='space-y-4 p-4'>
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame limitWidth={false} alwaysHideResumeWorkoutHeader={true}>
            <div className="flex items-center mb-6 gap-x-4">
                <h1 className="flex-1 min-w-0">
                    <p className='text-3xl font-bold max-w-[55vw] 2xl:max-w-[50vw] truncate'>
                        {activeWorkout.profileType === 'group' && activeWorkout.groupName && <>{activeWorkout.groupName} - </>}
                        {activeWorkout.profileType === 'individual' && activeWorkout.athleteName && <>{activeWorkout.athleteName} - </>}
                        {activeWorkout.workoutTemplate.name}
                    </p>
                </h1>

                <div className='w-40'>
                    <SolidButton
                        size="large"
                        text="Discard"
                        className='shadow-md'
                        action={() => DiscardActiveWorkoutWarningModal(activeWorkout.id, activeWorkout.profileType, activeWorkout.groupId, activeWorkout.athleteId, '/home')}
                    />
                </div>

                <div className='w-40'>
                    <BrandColorButton
                        size="large"
                        text="End"
                        className="shadow-md"
                        action={() => EndActiveWorkoutWarningModal(activeWorkout, '/home')}
                    />
                </div>
            </div>

            {activeWorkout.workoutTemplate.phases.map((phase, phaseIndex) => (
                <ContentFrame key={phaseIndex}>
                    <div className="w-full">
                        <PhaseHeader 
                            profileType={profileType}
                            groupId={groupId}
                            athleteId={athleteId}
                            phaseIndex={phaseIndex}
                            phase={phase}
                        />

                        {(() => {

                            const taperData = GetCurrentPhaseTaperData(activeWorkout, phase)
                            var items = []

                            if (phase.type === 'straight') {
                                phase.exercises.forEach((exercise, exerciseIndex) => {
                                    exercise.sets.forEach((set, setIndex) => {
                                        items.push({
                                            id: `phase-${phaseIndex}-straight-exercise-${exerciseIndex}-set-${setIndex}`,
                                            renderItem: (
                                                <ExerciseSetListItem
                                                    version="4.0"
                                                    profileType={profileType}
                                                    groupId={groupId}
                                                    groupAthletes={activeWorkout.groupAthletes}
                                                    athleteId={athleteId}
                                                    workoutType={activeWorkout.workoutType}
                                                    workoutTemplateKey={activeWorkout.workoutTemplateKey}
                                                    taperData={taperData}
                                                    phaseIndex={phaseIndex}
                                                    phase={phase}
                                                    exerciseIndex={exerciseIndex}
                                                    exercise={exercise}
                                                    setIndex={setIndex}
                                                    set={set}
                                                    MutateRemainingSets={MutateRemainingSets}
                                                />
                                            )
                                        })
                                    })
                                })
                            }

                            if (phase.type === 'circuit') {
                                Array.from({ length: phase.circuitRounds }, (_, roundIndex) => {
                                    items.push({
                                        id: `phase-${phaseIndex}-circuit-round-${roundIndex}`,
                                        renderItem: (
                                            <RoundDivider
                                                expanded={phase.expanded}
                                                roundIndex={roundIndex}
                                            />
                                        )
                                    })

                                    phase.exercises.forEach((exercise, exerciseIndex) => {
                                        items.push({
                                            id: `phase-${phaseIndex}-circuit-round-${roundIndex}-exercise-${exerciseIndex}`,
                                            renderItem: (
                                                <ExerciseSetListItem
                                                    version="4.0"
                                                    profileType={profileType}
                                                    groupId={groupId}
                                                    groupAthletes={activeWorkout.groupAthletes}
                                                    athleteId={athleteId}
                                                    workoutType={activeWorkout.workoutType}
                                                    workoutTemplateKey={activeWorkout.workoutTemplateKey}
                                                    taperData={taperData}
                                                    phaseIndex={phaseIndex}
                                                    phase={phase}
                                                    exerciseIndex={exerciseIndex}
                                                    exercise={exercise}
                                                    setIndex={roundIndex}
                                                    set={exercise.sets[roundIndex]}
                                                    MutateRemainingSets={MutateRemainingSets}
                                                />
                                            )
                                        })
                                    })
                                })
                            }

                            return (
                                <>
                                    <div className='w-full divide-y divide-gray-200 overflow-y-auto'>
                                        {items.map((item) => (
                                            <Fragment key={item.id}>
                                                {item.renderItem}
                                            </Fragment>
                                        ))}
                                    </div>
                                </>
                            )

                        })()}

                    </div>
                </ContentFrame>
            ))}
        </ScreenFrame>
    )
}
